import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { moneyCompact } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Building2, Search, ArrowDownToLine, Download, BellRing } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useRowSelection } from "@/hooks/useRowSelection";
import { BulkBar, BulkBtn, RowCheck } from "@/components/BulkBar";

const STANDING = {
  Proven: "bg-emerald-100 text-emerald-800",
  Tough: "bg-rose-100 text-rose-700",
  "Jobs stall": "bg-amber-100 text-amber-800",
  "Working on it": "bg-[#003E87]/10 text-[#003E87]",
  Unproven: "bg-slate-100 text-slate-600",
  Quiet: "bg-slate-100 text-slate-400",
};

// One number + its evidence. Withheld (—) where there is no evidence, never a fake zero.
function Stat({ label, value, sub, testid }) {
  return (
    <div data-testid={testid}>
      <div className="font-display text-lg font-bold text-black tabular-nums leading-none">{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-slate-400 mt-1">{label}</div>
      {sub && <div className="text-[10px] text-slate-400 leading-tight">{sub}</div>}
    </div>
  );
}

export default function Clients() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const navigate = useNavigate();
  const [owe, setOwe] = useState(new Set());
  const [chaseOnly, setChaseOnly] = useState(false);
  const sel = useRowSelection();

  useEffect(() => {
    api.get("/clients/standing").then((r) => setData(r.data)).catch(() => {});
    api.get("/clients/owe").then((r) => setOwe(new Set(r.data.owed || []))).catch(() => {});
  }, []);

  const exportClients = async () => {
    try {
      const res = await api.get("/export/clients", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "estimate_clients_export.xlsx"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not export"); }
  };

  const clients = useMemo(() => {
    if (!data) return [];
    let list = data.clients;
    const s = q.trim().toLowerCase();
    if (s) list = list.filter((c) => c.client.toLowerCase().includes(s));
    if (chaseOnly) list = list.filter((c) => owe.has(c.client));
    return list;
  }, [data, q, chaseOnly, owe]);

  const orderedIds = clients.map((c) => c.client);
  const allSelected = clients.length > 0 && sel.count === clients.length;

  const exportSelectedCsv = () => {
    const rows = clients.filter((c) => sel.isSelected(c.client));
    if (!rows.length) return;
    const q2 = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const header = ["Client", "Standing", "Pursuits", "Active", "Won", "Decided", "Award %", "Won value"];
    const lines = [header.join(",")];
    rows.forEach((c) => lines.push([
      q2(c.client), q2(c.standing), c.total ?? "", c.active ?? "", c.won ?? "",
      c.decided ?? "", c.award_pct ?? "", c.won_value ?? "",
    ].join(",")));
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "clients-selected.csv"; a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${rows.length} client${rows.length === 1 ? "" : "s"}`);
  };

  return (
    <div className="px-6 sm:px-8 py-8 pb-24">
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-black flex items-center gap-2">
            <Building2 className="w-5 h-5 text-[#003E87]" /> Clients
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            How bidding to each EPC actually goes. {data ? `Book win rate ${data.book_rate_pct}% across ${data.decided_book} decided.` : ""}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {owe.size > 0 && (
            <button onClick={() => setChaseOnly((v) => !v)} data-testid="clients-chase-toggle"
              className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-md border text-sm transition ${chaseOnly ? "border-[#F4C400] bg-[#F4C400]/15 text-[#8a6d00] font-medium" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
              <BellRing className="w-4 h-4" /> Chase queue ({owe.size})
            </button>
          )}
          <button onClick={exportClients} data-testid="export-clients" className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-slate-200 text-sm text-slate-600 hover:bg-slate-50">
            <ArrowDownToLine className="w-4 h-4" /> Export
          </button>
          <div className="relative w-64 max-w-full">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input data-testid="client-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a client…" className="pl-9" />
          </div>
        </div>
      </div>

      {!data ? (
        <div className="text-sm text-slate-400 py-16 text-center">Loading…</div>
      ) : clients.length === 0 ? (
        <Card className="p-12 text-center text-sm text-slate-500">
          {chaseOnly ? "No clients owe a reply right now — you're all caught up." : "No clients yet — they appear as soon as a bid carries a client name."}
        </Card>
      ) : (
        <>
        <div data-testid="clients-select-all" role="button" tabIndex={0} onClick={() => sel.selectAll(orderedIds)} className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800 mb-3 cursor-pointer">
          <RowCheck checked={allSelected} onClick={() => {}} testid="clients-select-all-check" />
          {allSelected ? "Clear all" : "Select all"}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {clients.map((c, i) => (
            <Card key={c.client} data-testid={`client-card-${c.client}`} onClick={() => navigate("/clients/" + encodeURIComponent(c.client))} className={`p-5 hover:shadow-md hover:border-[#003E87]/30 transition cursor-pointer ${sel.isSelected(c.client) ? "border-[#003E87] ring-1 ring-[#003E87]" : ""}`}>
              <div className="flex items-start justify-between gap-2 mb-4">
                <div className="min-w-0 flex items-start gap-2.5">
                  <div onClick={(e) => e.stopPropagation()} className="pt-0.5">
                    <RowCheck checked={sel.isSelected(c.client)} testid={`client-check-${c.client}`}
                      onClick={(e) => sel.toggle(c.client, i, e.shiftKey, orderedIds)} />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <div className="text-sm font-semibold text-black truncate">{c.client}</div>
                      {owe.has(c.client) && (
                        <span data-testid={`client-owe-ping-${c.client}`} title="Owes a reply"
                          className="w-2 h-2 rounded-full bg-[#F4C400] ring-2 ring-[#F4C400]/30 shrink-0" />
                      )}
                    </div>
                    <div className="text-xs text-slate-400 mt-0.5">{c.total} pursuit{c.total === 1 ? "" : "s"}{c.active ? ` · ${c.active} live` : ""}</div>
                  </div>
                </div>
                <span className={`text-[10px] font-semibold px-2 py-0.5 rounded shrink-0 ${STANDING[c.standing] || "bg-slate-100 text-slate-500"}`}>{c.standing}</span>
              </div>
              <div className="grid grid-cols-3 gap-y-4 gap-x-2">
                <Stat testid={`stat-record-${c.client}`}
                  label="Record"
                  value={c.decided > 0 ? `${c.won}/${c.decided}` : "—"}
                  sub={c.award_pct != null ? `${c.award_pct}% adj.` : "no decision yet"} />
                <Stat label="Live" value={c.active || "—"} sub={c.active ? "in play" : ""} />
                <Stat label="Longest wait" value={c.longest_wait_days != null ? `${c.longest_wait_days}d` : "—"} />
                <Stat label="Decides in" value={c.decides_in_days != null ? `${c.decides_in_days}d` : "—"}
                  sub={c.decided_samples ? `${c.decided_samples} sample${c.decided_samples === 1 ? "" : "s"}` : "not enough"} />
                <Stat label="Jobs stall" value={c.cancel_rate_pct != null ? `${c.cancel_rate_pct}%` : "—"}
                  sub={c.closed >= 3 ? `${c.cancelled}/${c.closed} closed` : "too few"} />
                <Stat label="Last contact" value={c.last_contact_days != null ? `${c.last_contact_days}d` : "—"} sub="ago" />
              </div>
              {c.won_value > 0 && (
                <div className="mt-4 pt-3 border-t border-slate-100 text-xs text-slate-500">
                  Won work: <span className="font-mono font-medium text-slate-700">{moneyCompact(c.won_value)}</span>
                </div>
              )}
            </Card>
          ))}
        </div>
        </>
      )}
      <BulkBar count={sel.count} onClear={sel.clear}>
        <BulkBtn testid="clients-bulk-export" icon={Download} onClick={exportSelectedCsv}>Export CSV</BulkBtn>
      </BulkBar>
    </div>
  );
}
